'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@rr/ui'
import { useCompanyCargos } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function CargosList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({ page, limit: PAGE_SIZE, search: search.trim() || undefined }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyCargos(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1) }}
          placeholder="Search by name or notes…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error ? error.message : 'Failed to load cargos.'
          : 'No cargos for this organization.'
      }
      pagination={<Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Weight (kg)</TableHead>
              <TableHead className="text-right">Volume (m³)</TableHead>
              <TableHead>Packing</TableHead>
              <TableHead>Transport</TableHead>
              <TableHead>Used in</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => {
              const usedLabel = c.usedIn
                ? `${c.usedIn.kind === 'quote' ? 'Quote' : 'Order'} ${c.usedIn.number ?? '—'}${c.usedIn.ref ? ` · ${c.usedIn.ref}` : ''}`
                : '—'
              return (
                <TableRow key={c.id}>
                  <TableCell className="font-medium text-foreground">
                    {c.cargoName ?? '—'}
                    {c.isPartial && (
                      <span className="ml-2 inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        Partial
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm tnum">
                    {c.weightKg != null ? Number(c.weightKg).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm tnum">
                    {c.volumeM3 != null ? Number(c.volumeM3).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.packingType?.name ?? '—'}
                    {c.packingCount != null ? ` × ${c.packingCount}` : ''}
                  </TableCell>
                  <TableCell className="text-sm">{c.transportType?.name ?? '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{usedLabel}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(c.createdAt)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}

